import { AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ImportTimesheetService } from 'app/core/services/import-timesheet.service';
import { PrivateService } from 'app/core/services/private.service';
import { RefreshTimesheetService } from 'app/core/services/refresh-timesheet/refresh-timesheet.service';
import jsPDF from 'jspdf';

@Component({
  selector: 'dialog-e-sign',
  templateUrl: './dialog-e-sign.component.html',
  styleUrls: ['./dialog-e-sign.component.scss']
})
export class DialogESignComponent implements OnInit, AfterViewInit {
  isLoading: boolean = false;
  @ViewChild('signatureCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  private ctx!: CanvasRenderingContext2D;
  private drawing = false;
  isSaved = false;
  hasSigned = false;

  constructor(public dialogRef: MatDialogRef<DialogESignComponent>, @Inject(MAT_DIALOG_DATA) public data: any,
    private importTimesheetService: ImportTimesheetService, private refreshTimesheetService: RefreshTimesheetService) { }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    const canvas = this.canvasRef.nativeElement;
    canvas.width = 500;
    canvas.height = 200;
    this.ctx = canvas.getContext('2d')!;
    this.ctx.lineWidth = 2;
    this.ctx.lineCap = 'round';
    this.ctx.strokeStyle = '#000';

    canvas.addEventListener('mousedown', this.startDraw);
    canvas.addEventListener('mousemove', this.draw);
    canvas.addEventListener('mouseup', this.endDraw);
    canvas.addEventListener('mouseleave', this.endDraw);

    // For touch support
    canvas.addEventListener('touchstart', this.startDraw);
    canvas.addEventListener('touchmove', this.draw);
    canvas.addEventListener('touchend', this.endDraw);
  }

  startDraw = (event: MouseEvent | TouchEvent) => {
    if (this.isSaved) return;
    event.preventDefault();
    this.drawing = true;
    this.ctx.beginPath();
    const { x, y } = this.getXY(event);
    this.ctx.moveTo(x, y);
  };

  draw = (event: MouseEvent | TouchEvent) => {
    if (!this.drawing || this.isSaved) return;

    const { x, y } = this.getXY(event);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();

    if (!this.hasSigned) {
      this.hasSigned = true;
    }
  };

  endDraw = (event: MouseEvent | TouchEvent) => {
    this.drawing = false;
    this.ctx.closePath();
  };

  getXY(event: MouseEvent | TouchEvent): { x: number; y: number } {
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    if (event instanceof MouseEvent) {
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    } else {
      const touch = event.touches[0] || event.changedTouches[0];
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    }
  }

  clearCanvas() {
    this.ctx.clearRect(0, 0, this.canvasRef.nativeElement.width, this.canvasRef.nativeElement.height);
    this.isSaved = false;
    this.hasSigned = false;
  }

  dataURLtoBlob(dataURL: string): Blob {
    const byteString = atob(dataURL.split(',')[1]);
    const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  }

  saveSignature() {
    this.isLoading = true;
    if (this.isSaved) return;

    const canvas = this.canvasRef.nativeElement;

    const scale = 0.75; // You can adjust this value
    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = canvas.width * scale;
    resizedCanvas.height = canvas.height * scale;

    const ctx = resizedCanvas.getContext('2d');

    // ✅ Fill with white background to avoid black areas in JPEG
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, resizedCanvas.width, resizedCanvas.height);

    // Draw original signature onto resized canvas
    ctx.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height);

    // Step 2: Export as low-quality JPEG to reduce file size
    const dataURL = resizedCanvas.toDataURL('image/jpeg', 0.3); // Try 0.2–0.4 for smaller sizes

    // Step 3: Generate PDF
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const imgProps = pdf.getImageProperties(dataURL);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    const margin = 10;
    pdf.addImage(dataURL, 'JPEG', margin, margin, pdfWidth - 2 * margin, pdfHeight);

    const employeeName = this.data.employeeName || 'Employee Name';
    const signedDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);

    pdf.text('Signed by:', pdfWidth / 2, pdfHeight + margin + 5, { align: 'center' });

    pdf.setFont(undefined, 'bold');
    pdf.text(employeeName, pdfWidth / 2, pdfHeight + margin + 12, { align: 'center' });

    // ➕ Add Date Signed below employee name
    pdf.setFont(undefined, 'normal');
    pdf.text(`Date Signed: ${signedDate}`, pdfWidth / 2, pdfHeight + margin + 20, { align: 'center' });

    const pdfBlob = pdf.output('blob');

    // Step 4: Upload PDF
    const timesheetId = this.data.selectedTimesheet;
    this.importTimesheetService.approveTimesheet(pdfBlob, timesheetId).subscribe({
      next: (res) => {
        this.refreshTimesheetService.emitRefresh(timesheetId);
        Promise.resolve().then(() => {
          this.isSaved = true;
          this.isLoading = false;
          this.dialogRef.close(true);
        });
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Upload failed:', err);
      }
    });
  }

  shrinkCanvas(originalCanvas: HTMLCanvasElement, scale = 0.25): HTMLCanvasElement {
    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = originalCanvas.width * scale;
    resizedCanvas.height = originalCanvas.height * scale;
    const ctx = resizedCanvas.getContext('2d');
    ctx?.drawImage(originalCanvas, 0, 0, resizedCanvas.width, resizedCanvas.height);
    return resizedCanvas;
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

}
